import { FC, ReactElement, ReactNode, useEffect, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  FiltersActions,
  FiltersActionsApply,
  FloatingMenuGrid,
  TableWrapperGeneric,
  useTableColumnsDynamic,
} from "@ryde/controls";
import {
  IPageHeaderContext,
  PageHeaderContext,
  useDynamicFormsFetch,
  usePageContext,
  usePageTitle,
} from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  bulkUpdateFormDrawerAF,
  IColumnDef,
  IExtendableDto,
  isEntityServerSideFiltered,
  ITableDto,
  Log,
  selectedTableRowsIdsAtomFamily,
  tableSettingsDrawerAF,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

import {
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
  PanelTop,
} from "../";
import {
  BulkUpdateFormDrawer,
  TableSettingsDrawerWrapperGeneric,
} from "../right-drawers";
import { ClientSideBulkOperationDrawerFlow } from "../right-drawers/client-side-bulk-operation-drawer-flow";

interface IPageClientSideFiltersProps {
  BottomWidgetComponent?: ReactElement;
  customRightComponent?: ReactElement | null;
  customHeaderBottomRightActions?: ReactNode;
  topWidgetComponent?: ReactNode;
}

export const PageClientSideFilters: FC<IPageClientSideFiltersProps> = ({
  BottomWidgetComponent = <PanelTop />,
  customRightComponent,
  customHeaderBottomRightActions,
  topWidgetComponent,
}) => {
  const { entity } = usePageContext();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const isServerSideFilteredEntity = isEntityServerSideFiltered(entity);

  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );
  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily(entity));
  const bulkUpdateFormDrawerOpened = useRecoilValue(
    bulkUpdateFormDrawerAF(entity),
  );
  const { initDynamicGridFetch, fetchTableRows } = useDynamicFormsFetch(entity);
  const { tableDefsGeneric } = useTableColumnsDynamic();
  const { pageTitleTranslated } = usePageTitle();

  const onFiltersApply = useEvent((): void => {
    fetchTableRows();
  });

  const headerContextValue: IPageHeaderContext = useMemo(() => {
    return {
      onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  useEffect(() => {
    initDynamicGridFetch();

    Log.debug(
      "calendarDates changed for client-side Date filters",
      calendarDates,
    );
    // calendarDates is used to trigger re-fetching when the date range changes for client-side dates filtered entities
  }, [initDynamicGridFetch, calendarDates]);

  return (
    <>
      <FinalSchemaService />
      <PageHeader
        mainTitle={pageTitleTranslated}
        bottomWidgetComponent={BottomWidgetComponent}
        customRightComponent={customRightComponent}
        customHeaderBottomRightActions={customHeaderBottomRightActions}
        topWidgetComponent={topWidgetComponent}
      >
        <PageHeaderContext.Provider value={headerContextValue}>
          <PageHeaderFilters
            Actions={
              isServerSideFilteredEntity ? (
                <FiltersActionsApply />
              ) : (
                <FiltersActions />
              )
            }
          />
        </PageHeaderContext.Provider>
      </PageHeader>
      <TableWrapperGeneric
        tableDefs={tableDefsGeneric as IColumnDef<IExtendableDto, unknown>[]}
      />

      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerWrapperGeneric<ITableDto>
          tableDefs={tableDefsGeneric}
        />
      )}
      {selectedIds && <FloatingMenuGrid />}
      {bulkUpdateFormDrawerOpened && <BulkUpdateFormDrawer />}

      {bulkOperationsDrawerOpened && <ClientSideBulkOperationDrawerFlow />}
    </>
  );
};
